import {
  Alignment,
  Button,
  Divider,
  Navbar,
  NavbarGroup,
  NavbarHeading,
  Tab,
  Tabs,
} from '@blueprintjs/core';
import { useContext, MouseEvent, useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import styles from './Navigation.module.css';
import ACELogoMono from '../../assets/ACE_logo_monochrome.svg';
import { OverflowTabs } from './OverflowTabs';
import { IApplication } from '../../store/state';
import { PortfolioNavigation } from '../PortfolioNavigation';
import { useTabs } from './useTabs';
import { useCloseApplication } from '../../hooks/navigation/useCloseApplication';
import { useIsDesktop } from '../../hooks/utils/useMediaQuery';
import { Restricted } from '@axo/shared/feature/restricted';
import { loan_application, permissions } from '@axo/shared/data-access/types';
import { ApplicationContext } from '../../store/context';
import { MarketCountrySelect } from './MarketCountrySelect';
import { LogInStatus } from './LogInStatus';
import { Locks } from './Locks';
import { HeartbeatStatus } from './HeartbeatStatus';
import { NavTab } from './NavTab';
import {
  ace_hooks,
  useApplicationsDetails,
} from '@axo/shared/data-access/hooks';
import { getProductIcon } from '../ApplicationCard/ProductIcon';
import { Notifications } from '../Notifications';
import { AssignmentType } from '@axo/shared/data-access/types/ace';
import { AssignApplicationsButton } from '../AssignApplicationsButton/AssignApplicationsButton';

function AceTabs({ marketCountry }: { marketCountry: string }) {
  const navigate = useNavigate();
  const tabLength = {
    small: 5,
    large: 10,
  };
  const location = useLocation();
  const { applicationID } = useParams();
  const isDesktop = useIsDesktop();
  const { visibleTabs, overflowTabs } = useTabs(
    marketCountry,
    isDesktop ? tabLength.large : tabLength.small
  );
  const close = useCloseApplication(marketCountry);

  function handleTabChange(newTabID: string) {
    if (newTabID === applicationID) return;
    const isApplicationPage = location.pathname.startsWith(`/application`);
    navigate(`/application/${newTabID}` + location.search, {
      replace: isApplicationPage,
    });
  }

  async function handleCloseTab(e: MouseEvent, application: IApplication) {
    e.stopPropagation();
    await close.closeAndUnlock(application.applicationID);
  }

  const details = useApplicationsDetails(
    visibleTabs.map((app) => app.applicationID)
  );

  return (
    <NavbarGroup>
      <Tabs
        animate
        id="navbar"
        large={false}
        onChange={handleTabChange}
        selectedTabId={applicationID ?? 0}
        defaultSelectedTabId={applicationID ?? 0}
        className={styles.tabs}
      >
        {visibleTabs.map((application) => (
          <Tab
            className={styles.hoverableTab}
            key={application.applicationID}
            id={application.applicationID}
            icon={getProductIcon(
              details
                .map((q) => q.data)
                .filter((app): app is loan_application.LoanApplication => !!app)
                .find((app) => app.ID === application.applicationID)?.Product
            )}
          >
            <NavTab
              isSelected={application.applicationID === applicationID}
              application={application}
              closeTab={handleCloseTab}
            />
          </Tab>
        ))}
      </Tabs>
      {overflowTabs.length > 0 && (
        <OverflowTabs
          data={overflowTabs}
          onSelect={(application) => handleTabChange(application.applicationID)}
          onClose={handleCloseTab}
        />
      )}
    </NavbarGroup>
  );
}

export function Navigation() {
  const { state } = useContext(DataAccessContext);
  const {
    state: { marketCountry },
  } = useContext(ApplicationContext);

  const allowedRoles = [
    permissions.RoleName.Manager,
    permissions.RoleName.Sales,
    permissions.RoleName.SecuredSales,
    permissions.RoleName.Telemarketing,
    permissions.RoleName.TelemarketingLimited,
  ];

  const { data } = ace_hooks.useGetAgentAssignmentV3(state.user.clientID);
  const isMLAgent = useMemo(
    () => data?.AssignmentTypes?.includes(AssignmentType.SecuredLoan) || false,
    [data]
  );

  return (
    <Navbar className={styles.navbar}>
      <NavbarGroup>
        <NavbarHeading>
          <Link to="/search">
            <Button minimal className={styles.logoContainer}>
              <img className={styles.logo} src={ACELogoMono} alt="ACE"></img>
            </Button>
          </Link>
        </NavbarHeading>
      </NavbarGroup>
      <Restricted isAllowedFor={allowedRoles}>
        {marketCountry && <AceTabs marketCountry={marketCountry} />}
      </Restricted>
      <NavbarGroup align={Alignment.END}>
        <div className={styles.rightNavGroup}>
          {isMLAgent ? (
            <AssignApplicationsButton />
          ) : (
            <Restricted
              isAllowedFor={[
                permissions.RoleName.Manager,
                permissions.RoleName.SecuredSales,
                permissions.RoleName.Sales,
              ]}
            >
              {marketCountry && (
                <PortfolioNavigation marketCountry={marketCountry} />
              )}
            </Restricted>
          )}
          <Divider className={styles.rightNavDivider} />
          {marketCountry && <Notifications marketCountry={marketCountry} />}
          <MarketCountrySelect />
          <Restricted isAllowedFor={allowedRoles}>
            {marketCountry && <Locks marketCountry={marketCountry} />}
          </Restricted>
          <LogInStatus user={state.user} />
          <HeartbeatStatus />
        </div>
      </NavbarGroup>
    </Navbar>
  );
}
